import { Fragment, useEffect, useRef, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import useGetHook from "../../../customHooks/useGetHook";
import { tw } from "@careinfo/tailwind";
import { useDebounce } from "../../../customHooks/useDebounceHook";

type ComboboxInputProps = {
  url: string;
  queryKey: string;
  props?: Record<string, any>;
  name: string;
  label?: string;
  placeholder?: string;
  errors?: any;
  disabled?: boolean;

  /** react-hook-form */
  setValue: any;
  getValues: any;
  clearErrors: any;

  /** NEW */
  returnObject?: boolean;
};

export default function ComboboxInput({
  url,
  queryKey,
  props: otherProps,
  name,
  label,
  placeholder,
  errors,
  disabled = false,
  setValue,
  getValues,
  clearErrors,
  returnObject = false,
}: ComboboxInputProps) {
  const limit = 10;
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);

  const debouncedQuery = useDebounce(query);

  /* -------------------- FETCH DATA -------------------- */
  const { data: serverData } = useGetHook({
    queryKey: [queryKey, debouncedQuery],
    url,
    params: {
      limit,
      offset: 0,
      search: debouncedQuery,
      ...otherProps,
    },
  });

  useEffect(() => {
    if (Array.isArray(serverData?.results)) {
      setData(serverData.results);
    }
  }, [serverData]);

  /* -------------------- SYNC DEFAULT VALUE -------------------- */
  useEffect(() => {
    const formValue = getValues(name);

    if (!formValue) {
      setSelected(null);
      return;
    }

    // If object already
    if (typeof formValue === "object") {
      setSelected(formValue);
      return;
    }

    // If ID, find matching object
    const match = data.find((item) => item.id === formValue);
    if (match) {
      setSelected(match);
    }
  }, [getValues(name), data]);

  /* -------------------- HANDLE CHANGE -------------------- */
  const handleChange = (item: any) => {
    setSelected(item);
    clearErrors(name);

    if (returnObject) {
      setValue(name, item);
    } else {
      setValue(name, item?.id ?? null);
    }
  };

  /* -------------------- CLEAR -------------------- */
  const handleClear = (e: any) => {
    e.stopPropagation();
    setSelected(null);
    setValue(name, null);
  };

  const filteredData =
    query === ""
      ? data
      : data.filter((item: any) =>
          item.name
            ?.toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  return (
    <Combobox value={selected} onChange={handleChange} disabled={disabled}>
      <div className={tw`relative mt-3`}>
        {label && <label className={tw`text-sm`}>{label}</label>}

        <div className={tw`relative mt-2`}>
          <Combobox.Input
            className={tw`w-full bg-[#EEF5F4] p-2 focus:outline-none`}
            placeholder={placeholder}
            displayValue={(item: any) => item?.name ?? ""}
            onChange={(e) => setQuery(e.target.value)}
            onBlur={() => clearErrors(name)}
            onClick={() => buttonRef.current?.click()}
          />

          <Combobox.Button
            ref={buttonRef}
            className={tw`absolute inset-y-0 right-0 flex items-center pr-2`}
          >
            {selected && (
              <PlusIcon
                className={tw`h-4 w-4 text-gray-400 rotate-45 mr-1`}
                onClick={handleClear}
              />
            )}
            <ChevronDownIcon className={tw`h-5 w-5 text-gray-400`} />
          </Combobox.Button>
        </div>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery("")}
        >
          <Combobox.Options
            className={tw`absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white shadow-lg`}
          >
            {filteredData.length === 0 && query !== "" ? (
              <div className={tw`py-2 px-4 text-gray-500`}>
                Nothing found.
              </div>
            ) : (
              filteredData.map((item: any) => (
                <Combobox.Option
                  key={item.id}
                  value={item}
                  className={({ active }) =>
                    tw`cursor-pointer select-none py-2 pl-4 pr-4 ${
                      active ? tw`bg-primary text-white` : tw`text-gray-900`
                    }`
                  }
                >
                  {item.name}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>

        {errors && (
          <p className={tw`text-red-500 text-xs mt-1`}>
            {errors.message}
          </p>
        )}
      </div>
    </Combobox>
  );
}
